import React from "react";
import useCompanyTerms from "utils/getCompanyInfo";

interface PrivacyPolicyPopupProps {
  onClose: () => void;
  internalId: string;
}

const PrivacyPolicyPopup: React.FC<PrivacyPolicyPopupProps> = ({ onClose, internalId }) => {
  const {terms} = useCompanyTerms();
  const companyTerms = terms;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
    <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 max-w-3xl w-full overflow-y-auto max-h-[80vh]">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200">
          Privacy Policy 
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
          Close
        </button>
      </div>

      {/* Privacy Policy Content */}
      <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-4">
        <p>
          Welcome to {companyTerms.companyName} booking platform (the “Platform” or “We”). The Platform
          allows guests or tenants to perform online booking or reservations of
          properties and rooms (the "Service").
        </p>
        <p>
          This Privacy & Cookie Use Policy (the “Policy”) outlines our practices
          regarding the collection, storage, use, and disclosure of your
          personal information and data. By using the Service, you consent to
          this Policy.
        </p>
        <p>
          Providing personal information is your choice. However, certain
          information is required to allow you to use the Service. As data
          controllers, we are responsible for the collection and use of any data
          you submit through the Service.
        </p>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          1. Personal Information We Collect
        </h3>
        <ul className="list-disc ml-6 space-y-1">
          <li>Identification & contact details (name, address, phone, email)</li>
          <li>
            Payment information (credit/debit card details processed securely by
            our payment processor)
          </li>
          <li>
            Technical information (cookies, IP address, browser/device data)
          </li>
          <li>
            Bank details if required for property owners, and communications you
            send us
          </li>
        </ul>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          2. Use & Disclosure of Information
        </h3>
        <p>We may use and share your data to:</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Process bookings and payments</li>
          <li>Communicate with you and respond to inquiries</li>
          <li>Improve the functionality and security of the Service</li>
          <li>Conduct analytics, bug detection, and product development</li>
          <li>Comply with applicable laws and assist competent authorities</li>
        </ul>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          3. Your Rights
        </h3>
        <p>
          You have rights to access, correct, delete, restrict, or export your
          personal data. You may also withdraw consent at any time and object to
          processing based on legitimate interests or direct marketing.
        </p>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          4. GDPR Notice
        </h3>
        <p>
          We process data in compliance with the EU General Data Protection
          Regulation (GDPR). Legal bases include consent, contractual necessity,
          legitimate interests, and compliance with legal obligations.
        </p>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          5. Data Retention
        </h3>
        <p>
          We retain personal data only as long as necessary for the purposes
          described in this Policy, after which it may be securely deleted.
        </p>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          6. CCPA Notice (California Residents)
        </h3>
        <ul className="list-disc ml-6 space-y-1">
          <li>Right to Know what personal data we collect and share</li>
          <li>Right to Request deletion of personal data</li>
          <li>Right to Non-Discrimination for exercising rights</li>
          <li>
            Do Not Sell My Information: we do not sell personal identifiers,
            though advertising networks may collect usage data
          </li>
        </ul>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          7. Children’s Privacy
        </h3>
        <p>
          The Service is intended for individuals aged 16 or older. We do not
          knowingly collect data from children under 16.
        </p>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          8. Cookies
        </h3>
        <p>
          We use cookies to improve website functionality, security, and user
          experience. You can manage cookies through your browser settings or
          opt-out of advertising cookies at{" "}
          <a
            href="https://optout.aboutads.info/"
            className="text-blue-600 dark:text-blue-400 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            optout.aboutads.info
          </a>
          .
        </p>

        <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
          9. Changes to This Policy
        </h3>
        <p>
          We may update this Policy from time to time. Updates will be posted on
          the Service. Continued use of the Service constitutes acceptance of
          the revised Policy.
        </p>

                  <h3 className="font-semibold text-neutral-800 dark:text-neutral-200">
            10. Contact Us
          </h3>
          <p>
            If you have questions about this Policy, please contact us:
            <br />
            <strong>{companyTerms.companyName}</strong>  
            <br />
            📧{" "}
            <a
              href={`mailto:${companyTerms.contactEmail}`}
              className="text-blue-600 dark:text-blue-400 underline"
            >
              {companyTerms.contactEmail}
            </a>
          </p>
      </div>
    </div>
  </div>
  );
};

export default PrivacyPolicyPopup;